/**
 * API client for the A5Ai Bridge chat endpoint.
 *
 * The AI Bridge runs on port 5291 and provides an SSE-based chat interface.
 * The frontend forwards the same JWT token and workspace ID used for the .NET API.
 * Auth: Bearer header from the in-memory token when present; otherwise the
 * httpOnly a5core_token cookie (sent via credentials: 'include' — cookies are
 * host-scoped, so the different port still receives it).
 */

import { getToken } from './client';

// House pattern (askChat/documents/insights): VITE_AI_BRIDGE_URL override, dev
// hits the local bridge by port, PROD uses the same-origin /ai prefix (nginx
// routes it to the bridge container). The old bare http://host:5291 fallback
// shipped to prod once — mixed content + a port that isn't public → every
// CopilotPanel message died with "[Error: Failed to fetch]".
const AI_BRIDGE_URL: string =
  import.meta.env.VITE_AI_BRIDGE_URL
  ?? (import.meta.env.DEV ? `http://${window.location.hostname}:5291` : '/ai');
const WORKSPACE_ID_KEY = 'a5core_workspace_id';

/** Auth headers for AiBridge requests: Bearer when we hold a token (the cookie covers reloads). */
function aiAuthHeaders(): Record<string, string> {
  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

export interface ChatEvent {
  type: 'text' | 'tool_use' | 'tool_result' | 'action' | 'status' | 'done' | 'error' | 'pending_confirmation';
  content?: string;
  toolName?: string;
  input?: unknown;
  result?: unknown;
  // pending_confirmation: write-tool confirmation gate (Privacy Policy §11).
  // The UI MUST render Approve / Decline buttons that call approvePendingWrite /
  // declinePendingWrite respectively. The actual write does not happen until
  // the user clicks Approve.
  confirmationToken?: string;
  summary?: string;
  expiresInSeconds?: number;
  action?: {
    actionType: 'navigate' | 'ui_click' | 'ui_fill' | 'activate_workspace';
    path?: string;
    reason: string;
    target?: string;
    value?: string;
    formData?: {
      fields?: Record<string, unknown>;
      lines?: unknown[];
      explanation?: string;
    };
  };
}

export interface ScreenData {
  summary: string;
  recordCount?: number;
  filters?: Record<string, string>;
  preview?: string[];
  tableData?: { headers: string[]; rows: string[][]; totalRows?: number }[];
  activeRecord?: { type?: string; fields: Record<string, string>; rawData?: string };
  actions?: { id: string; label: string }[];
  formFields?: { name: string; label: string; type: string; value: string }[];
  updatedAt?: number;
}

/**
 * Structured Darbai card payload sent with the "Ištirkite" message. The
 * bridge pre-fetches evidence for it BEFORE the first LLM call, so the first
 * reply is grounded in real numbers instead of the card title.
 */
export interface WorkItemContext {
  category?: string;
  agentName?: string;
  title?: string;
  severity?: string | null;
  amount?: number | null;
  daysOverdue?: number | null;
  dueDate?: string | null;
  personId?: number | null;
  personName?: string | null;
  documentNumber?: string | null;
  invoiceId?: number | null;
  journalEntryId?: number | null;
  description?: string | null;
  suggestedAction?: string | null;
  stableKey?: string | null;
}

export interface ChatOptions {
  message: string;
  conversationId?: string;
  currentPage?: string;
  screenData?: ScreenData;
  workItemContext?: WorkItemContext;
  messageHistory?: Array<{ role: 'user' | 'assistant'; content: string }>;
  onEvent: (event: ChatEvent) => void;
  onError?: (error: Error) => void;
  onComplete?: () => void;
}

/**
 * Send a chat message and stream the response via SSE.
 */
export async function streamChat(options: ChatOptions): Promise<void> {
  const workspaceId = localStorage.getItem(WORKSPACE_ID_KEY);

  try {
    const response = await fetch(`${AI_BRIDGE_URL}/chat`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...aiAuthHeaders(),
        'X-Workspace-Id': workspaceId || '',
      },
      body: JSON.stringify({
        message: options.message,
        conversationId: options.conversationId,
        currentPage: options.currentPage,
        screenData: options.screenData,
        workItemContext: options.workItemContext,
        messageHistory: options.messageHistory,
      }),
    });

    if (!response.ok) {
      const body = await response.text().catch(() => '');
      throw new Error(`AI Bridge error ${response.status}: ${body}`);
    }

    // Extract conversation ID from response headers
    const convId = response.headers.get('X-Conversation-Id');

    // Read the SSE stream
    const reader = response.body?.getReader();
    if (!reader) throw new Error('No response stream');

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Parse SSE events (data: {...}\n\n)
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const event: ChatEvent = JSON.parse(line.slice(6));
            // Attach conversation ID to all events
            (event as ChatEvent & { conversationId?: string }).conversationId = convId || undefined;
            options.onEvent(event);

            if (event.type === 'done' && options.onComplete) {
              options.onComplete();
            }
            if (event.type === 'error' && options.onError) {
              options.onError(new Error(event.content || 'Unknown error'));
            }
          } catch {
            // Skip malformed JSON
          }
        }
      }
    }
  } catch (err) {
    if (options.onError) {
      options.onError(err instanceof Error ? err : new Error(String(err)));
    }
    throw err;
  }
}

export interface ChatSession {
  id: string;
  messageCount: number;
  createdAt: number;
  lastActivity: number;
  preview: string;
}

/**
 * List all chat sessions from the AiBridge.
 */
export async function listSessions(): Promise<ChatSession[]> {
  const workspaceId = localStorage.getItem(WORKSPACE_ID_KEY);

  const response = await fetch(`${AI_BRIDGE_URL}/chat/sessions`, {
    credentials: 'include',
    headers: {
      ...aiAuthHeaders(),
      'X-Workspace-Id': workspaceId || '',
    },
  });

  if (!response.ok) return [];
  return response.json();
}

/**
 * Delete a chat session.
 */
export async function deleteSession(sessionId: string): Promise<boolean> {
  const workspaceId = localStorage.getItem(WORKSPACE_ID_KEY);

  const response = await fetch(`${AI_BRIDGE_URL}/chat/sessions/${sessionId}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      ...aiAuthHeaders(),
      'X-Workspace-Id': workspaceId || '',
    },
  });

  return response.ok;
}

/**
 * Check if the AI Bridge is healthy.
 */
export async function checkAiBridgeHealth(): Promise<{ healthy: boolean; status?: string }> {
  try {
    const response = await fetch(`${AI_BRIDGE_URL}/health`);
    if (response.ok) {
      const data = await response.json();
      return { healthy: true, status: data.status };
    }
    return { healthy: false, status: `HTTP ${response.status}` };
  } catch (err) {
    return { healthy: false, status: err instanceof Error ? err.message : 'Unknown error' };
  }
}

// ─── Pending-write confirmation (Privacy Policy §11) ───────────────────────
//
// Write tools (create_invoice, post_invoice, delete_invoice, create_journal_entry,
// create_bank_operation) are gated behind a pending-state store. When the AI
// calls a write tool, the backend emits a `pending_confirmation` SSE event
// with a token. The UI renders Approve / Decline buttons that POST to these
// endpoints. The actual write does NOT happen until the user clicks Approve.

export interface PendingWriteResult {
  status: 'executed' | 'declined';
  toolName?: string;
  result?: unknown;
}

/**
 * Approve a pending write. The backend executes the tool with the original
 * user's auth context (captured at pending-create time), so the audit trail
 * correctly records the user as the one who clicked Approve.
 *
 * Returns the tool execution result. Throws on 404 (token missing/expired/
 * already used) — the UI should surface this to the user.
 */
export async function approvePendingWrite(token: string): Promise<PendingWriteResult> {
  const workspaceId = localStorage.getItem(WORKSPACE_ID_KEY);

  const response = await fetch(`${AI_BRIDGE_URL}/approve`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...aiAuthHeaders(),
      'X-Workspace-Id': workspaceId || '',
    },
    body: JSON.stringify({ token }),
  });

  if (!response.ok) {
    const body = await response.text().catch(() => '');
    if (response.status === 404) {
      throw new Error('This confirmation has expired or already been used. Please ask the assistant again.');
    }
    throw new Error(`Approve failed (${response.status}): ${body}`);
  }

  return response.json();
}

/**
 * Decline a pending write. Idempotent — declining a missing or already-used
 * token returns 404, which we surface as success to the caller (the user's
 * intent "I don't want to do this" is satisfied either way).
 */
export async function declinePendingWrite(token: string, reason?: string): Promise<void> {
  const workspaceId = localStorage.getItem(WORKSPACE_ID_KEY);

  const response = await fetch(`${AI_BRIDGE_URL}/decline`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...aiAuthHeaders(),
      'X-Workspace-Id': workspaceId || '',
    },
    body: JSON.stringify({ token, reason }),
  });

  if (!response.ok && response.status !== 404) {
    const body = await response.text().catch(() => '');
    throw new Error(`Decline failed (${response.status}): ${body}`);
  }
  // 404 on decline = already gone, treat as success.
}

export interface PendingWriteInfo {
  token: string;
  toolName: string;
  summary: string;
  createdAt: number;
  expiresAt: number;
}

/**
 * List the current user's pending writes (for UI display of the
 * confirmation queue after a page reload).
 */
export async function listPendingWrites(): Promise<PendingWriteInfo[]> {
  const workspaceId = localStorage.getItem(WORKSPACE_ID_KEY);

  const response = await fetch(`${AI_BRIDGE_URL}/pending`, {
    credentials: 'include',
    headers: {
      ...aiAuthHeaders(),
      'X-Workspace-Id': workspaceId || '',
    },
  });

  if (!response.ok) return [];
  const data = await response.json();
  return data.pending ?? [];
}
